import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Share2, Navigation, Shield, Hourglass } from "lucide-react";
import { PROFILES, computeResult } from "../data/quiz";
import { LeadCapture } from "./LeadCapture";

const EASE = [0.22, 1, 0.36, 1];

const INTENSITY_NOTES = {
  REDUSĂ:
    "Utilizarea ta neintenționată pare redusă: telefonul îți servește mai degrabă un scop decât să te tragă de el.",
  MODERATĂ:
    "Utilizarea neintenționată apare în momente specifice: oboseală, așteptare sau evitare. Este un teren bun pentru schimbări mici.",
  RIDICATĂ:
    "Utilizarea neintenționată e frecvent prezentă: reflexul de verificare, pierderea timpului și întreruperile apar des. Vestea bună: exact aici intervențiile mici funcționează cel mai repede.",
};

// Accent vizual diferit pentru fiecare profil: Pilot = electric blue, Refugiu = teal luminos, Amânare = cyan rece.
const PROFILE_ACCENTS = ["#38A8FF", "#20D6C7", "#67E8F9"];
const PROFILE_ICONS = [Navigation, Shield, Hourglass];

function Row({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-teal/25 bg-surface p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-soft">{label}</p>
      <p data-testid={testid} className="mt-1.5 leading-relaxed text-[#F7FAFC]">
        {value}
      </p>
    </div>
  );
}

export function Result({ answers, screenTime }) {
  const reduce = useReducedMotion();
  const headingRef = useRef(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  const result = computeResult(answers);
  const profile = PROFILES[result.primary];
  const accent = PROFILE_ACCENTS[result.primary];
  const ProfileIcon = PROFILE_ICONS[result.primary];

  const screenTimeLower = screenTime ? screenTime.charAt(0).toLowerCase() + screenTime.slice(1) : "";

  const plan = [
    { day: "Ziua 1", title: "Observă", text: "Notează de 3 ori când ai deschis telefonul fără un motiv clar." },
    { day: "Ziua 2", title: "Creează fricțiune", text: "Scoate aplicația principală de social media de pe primul ecran și oprește notificările neesențiale." },
    { day: "Ziua 3", title: "Înlocuiește", text: "Folosește acțiunea recomandată pentru profilul tău înainte de primul scroll automat." },
  ];

  const share = async () => {
    const url = window.location.origin;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Quiz MindRegen", text: "Îți controlezi telefonul sau telefonul îți controlează atenția?", url });
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const reveal = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: 0.55, ease: EASE, delay },
  });

  return (
    <main data-testid="result-screen" className="pt-12 sm:pt-16">
      <motion.h1
        {...reveal(0)}
        ref={headingRef}
        tabIndex={-1}
        className="text-[12px] font-semibold uppercase tracking-[0.22em] text-teal outline-none"
      >
        Rezultatul tău MindRegen
      </motion.h1>

      <motion.section {...reveal(0.05)} data-testid="intensity-section" className="mt-8 rounded-3xl border border-teal/25 bg-surface p-6 sm:p-7">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-soft">
          Utilizare neintenționată
        </h2>
        <p
          data-testid="intensity-value"
          className="glow-soft mt-2 inline-block rounded-xl font-display text-4xl font-bold text-teal sm:text-5xl"
        >
          {result.intensity}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-soft" data-testid="intensity-note">
          {INTENSITY_NOTES[result.intensity]}
        </p>
      </motion.section>

      <motion.section {...reveal(0.08)} data-testid="pattern-section" className="mt-6 rounded-3xl border border-teal/25 bg-surface p-6 sm:p-7">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-soft">
          Tipar dominant
        </h2>
        <div className="mt-3 flex items-center gap-3.5">
          <span
            aria-hidden="true"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
            style={{ backgroundColor: `${accent}1A`, color: accent }}
          >
            <ProfileIcon className="h-5 w-5" />
          </span>
          <p data-testid="pattern-name" className="font-display text-2xl font-bold sm:text-3xl" style={{ color: accent }}>
            {profile.name}
          </p>
        </div>
        {result.secondary !== null && (
          <p data-testid="pattern-secondary" className="mt-3 text-sm text-soft">
            Tipar secundar: {PROFILES[result.secondary].name}
          </p>
        )}
      </motion.section>

      <motion.section {...reveal(0.1)} data-testid="pattern-chart" className="mt-6 rounded-3xl border border-teal/25 bg-surface p-6 sm:p-7">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-soft">
          Cum se distribuie răspunsurile tale
        </h2>
        <div className="mt-6 space-y-5">
          {PROFILES.map((p, i) => {
            const share = result.total ? result.counts[i] / result.total : 0;
            const filled = Math.round(share * 10);
            const isPrimary = i === result.primary;
            const isSecondary = i === result.secondary;
            const RowIcon = PROFILE_ICONS[i];
            return (
              <div key={i}>
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-xs font-medium text-soft sm:text-sm">
                    <RowIcon
                      aria-hidden="true"
                      className="h-3.5 w-3.5"
                      style={{ color: isPrimary || isSecondary ? PROFILE_ACCENTS[i] : undefined }}
                    />
                    {p.name}
                  </span>
                  {isPrimary && (
                    <span
                      className="text-[10px] font-semibold uppercase tracking-[0.18em]"
                      style={{ color: PROFILE_ACCENTS[i] }}
                    >
                      Tiparul tău
                    </span>
                  )}
                </div>
                <div
                  role="img"
                  aria-label={`${p.name}: ${filled} din 10`}
                  className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-white/10"
                >
                  <motion.div
                    className="h-full rounded-full"
                    style={{
                      backgroundColor: isPrimary || isSecondary ? PROFILE_ACCENTS[i] : "rgba(255,255,255,0.28)",
                      opacity: isSecondary ? 0.55 : 1,
                    }}
                    initial={reduce ? false : { width: 0 }}
                    animate={{ width: `${filled * 10}%` }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </motion.section>

      <motion.section {...reveal(0.1)} data-testid="profile-section" className="mt-10">
        <span aria-hidden="true" className="block h-1 w-12 rounded-full" style={{ backgroundColor: accent }} />
        <p data-testid="profile-headline" className="mt-5 font-display text-2xl font-bold leading-snug text-ink sm:text-3xl">
          {profile.headline}
        </p>
        <p data-testid="profile-explanation" className="mt-4 leading-relaxed text-soft">
          {profile.explanation}
        </p>
        <div className="mt-6 space-y-3">
          <Row label="Semnal" value={profile.signal} testid="profile-signal" />
          <Row label="Ce te ajută" value={profile.lever} testid="profile-lever" />
        </div>
        <div
          data-testid="profile-action"
          className="glow-soft mt-4 rounded-2xl border p-5"
          style={{ borderColor: `${accent}59`, backgroundColor: `${accent}12` }}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: accent }}>
            Acțiunea ta de 24 de ore
          </p>
          <p className="mt-2 leading-relaxed text-[#F7FAFC]">{profile.action}</p>
        </div>
      </motion.section>

      {screenTime && screenTime !== "Nu știu" && (
        <motion.section {...reveal(0.1)} data-testid="reality-check-result" className="mt-10 rounded-3xl border border-teal/25 bg-surface p-6">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-soft">
            Reality check
          </h2>
          <p data-testid="reality-check-text" className="mt-3 leading-relaxed text-[#F7FAFC]">
            Ai raportat aproximativ {screenTimeLower} pe telefon ieri.
          </p>
          <p className="mt-2 text-sm leading-relaxed text-soft">
            Timpul total este doar un reper. Contează mai ales cât de intenționat folosești
            telefonul și dacă îți afectează concentrarea, somnul sau lucrurile importante.
          </p>
        </motion.section>
      )}

      <motion.section {...reveal(0.1)} data-testid="plan-section" className="mt-10">
        <h2 className="font-display text-2xl font-bold">Planul tău MindRegen</h2>
        <div className="mt-5 space-y-4">
          {plan.map((day, i) => (
            <div
              key={i}
              data-testid={`plan-day-${i + 1}`}
              className="rounded-2xl border border-teal/25 bg-surface p-5"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal">
                {day.day} · {day.title}
              </p>
              <p className="mt-2 leading-relaxed text-[#F7FAFC]">{day.text}</p>
            </div>
          ))}
        </div>
      </motion.section>

      <LeadCapture profileKey={profile.key} intensity={result.intensity} screenTime={screenTime} />

      <motion.section {...reveal(0.1)} data-testid="share-section" className="mt-10 border-t border-teal/20 pt-8 text-center">
        <p className="text-base font-medium leading-snug text-soft">
          Știi pe cineva care spune mereu «mai stau doar 5 minute»?
        </p>
        <button
          type="button"
          data-testid="share-button"
          onClick={share}
          className="mt-4 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full border border-teal/40 bg-transparent px-8 font-display text-[14px] font-bold uppercase tracking-wide text-teal transition-colors duration-200 hover:bg-teal/10 sm:w-auto"
        >
          <Share2 className="h-4 w-4" aria-hidden="true" />
          Distribuie quiz-ul
        </button>
        <p aria-live="polite" data-testid="share-feedback" className="mt-3 text-sm text-teal empty:hidden">
          {copied ? "Link copiat ✓" : ""}
        </p>
      </motion.section>

      <motion.section {...reveal(0.1)} data-testid="final-cta" className="mt-12 text-center">
        <figure
          data-testid="final-photo"
          className="relative mx-auto max-w-[440px] overflow-hidden rounded-3xl border border-white/10"
        >
          <img
            src="/final-calm.jpg"
            alt="O mână lasă telefonul cu ecranul în jos pe o masă din lemn, într-o cameră liniștită cu lumină naturală"
            loading="lazy"
            className="h-52 w-full object-cover sm:h-64"
          />
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-t from-base/70 via-transparent to-base/15" />
        </figure>
        <p className="mx-auto mt-10 max-w-[40ch] font-display text-xl font-semibold leading-relaxed text-ink sm:text-2xl">
          Controlul atenției nu se câștigă într-o singură zi.
          <br />
          Se reconstruiește prin pași mici, repetați.
        </p>
        <p className="mt-8 font-display text-2xl font-bold text-teal">MindRegen</p>
        <p className="mt-4 text-sm leading-relaxed text-soft">
          Recâștigă-ți atenția.
          <br />
          Recâștigă-ți timpul.
          <br />
          Recâștigă controlul.
        </p>
      </motion.section>
    </main>
  );
}
